<template>
  <div>
    <!-- Summary Cards -->
    <div class="row" v-if="summary && isGraph">
      <div class="col-lg-3 col-xs-6">
        <div class="small-box bg-aqua">
          <div class="inner">
            <h3>{{ formatCurrency(summary.totalInvoices) }}</h3>
            <p>Total Invoices</p>
          </div>
          <div class="icon">
            <i class="fa fa-money"></i>
          </div>
          <a href="#" class="small-box-footer">&nbsp;</a>
        </div>
      </div>
      <div class="col-lg-3 col-xs-6">
        <div class="small-box bg-red">
          <div class="inner">
            <h3>{{ formatCurrency(summary.totalExpenses) }}</h3>
            <p>Total Expenses</p>
          </div>
          <div class="icon">
            <i class="fa fa-credit-card"></i>
          </div>
          <a href="#" class="small-box-footer">&nbsp;</a>
        </div>
      </div>
      <div class="col-lg-3 col-xs-6">
        <div class="small-box bg-green">
          <div class="inner">
            <h3>{{ formatCurrency(summary.totalProfit) }}</h3>
            <p>Net Profit/Loss</p>
          </div>
          <div class="icon">
            <i class="fa fa-line-chart"></i>
          </div>
          <a href="#" class="small-box-footer">&nbsp;</a>
        </div>
      </div>
      <div class="col-lg-3 col-xs-6">
        <div class="small-box bg-yellow">
          <div class="inner">
            <h3>{{ summary.overallProfitPercent.toFixed(2) }}%</h3>
            <p>Overall Profit %</p>
          </div>
          <div class="icon">
            <i class="fa fa-percent"></i>
          </div>
          <a href="#" class="small-box-footer">&nbsp;</a>
        </div>
      </div>
    </div>

    <!-- Charts Row -->
    <div class="row">
      <!-- Revenue vs Expenses Chart -->
      <div class="col-md-8" v-if="isGraph">
        <div class="box box-primary">
          <div class="box-header with-border">
            <h3 class="box-title">Monthly Revenue vs Expenses Trend</h3>
          </div>
          <div class="box-body">
            <apexchart
              type="line"
              height="350"
              :options="revenueExpensesChartData.options"
              :series="revenueExpensesChartData.series"
              v-if="records.length > 0"
            ></apexchart>
            <div v-else style="height: 350px; display: flex; align-items: center; justify-content: center; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9;">
              <div style="text-align: center; color: #666;">
                <i class="fa fa-bar-chart fa-2x"></i>
                <p style="margin-top: 10px;">No data available</p>
                <small>Please load data to view charts</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Profit/Loss Distribution -->
      <div class="col-md-4" v-if="isGraph">
        <div class="box box-warning">
          <div class="box-header with-border">
            <h3 class="box-title">Monthly Performance Distribution</h3>
          </div>
          <div class="box-body">
            <apexchart
              type="pie"
              height="350"
              :options="performanceDistributionChartData.options"
              :series="performanceDistributionChartData.series"
              v-if="records.length > 0"
            ></apexchart>
            <div v-else style="height: 350px; display: flex; align-items: center; justify-content: center; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9;">
              <div style="text-align: center; color: #666;">
                <i class="fa fa-pie-chart fa-2x"></i>
                <p style="margin-top: 10px;">No data available</p>
                <small>Please load data to view charts</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Profit Percentage Trend -->
    <div class="row">
      <div class="col-md-12" v-if="isGraph">
        <div class="box box-info">
          <div class="box-header with-border">
            <h3 class="box-title">Monthly Profit Percentage Trend</h3>
          </div>
          <div class="box-body">
            <apexchart
              type="area"
              height="350"
              :options="profitPercentageChartData.options"
              :series="profitPercentageChartData.series"
              v-if="records.length > 0"
            ></apexchart>
            <div v-else style="height: 350px; display: flex; align-items: center; justify-content: center; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9;">
              <div style="text-align: center; color: #666;">
                <i class="fa fa-area-chart fa-2x"></i>
                <p style="margin-top: 10px;">No data available</p>
                <small>Please load data to view charts</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Data Table -->
    <div class="box box-default">
      <div class="box-header with-border">
        <h3 class="box-title">Monthly Growth Data</h3>
        <div class="box-tools pull-right">
          <button type="button" class="btn btn-success btn-sm" @click="exportToExcel">
            <i class="fa fa-download"></i> Export Excel
          </button>
        </div>
      </div>
      <div class="box-body">
        <b-table
          show-empty
          stacked="md"
          responsive
          :fields="tableFields"
          :items="paginatedRecords"
          :tbody-tr-class="getRowClass"
          class="table-background position-relative"
          :per-page="perPage"
          :current-page="currentPage"
        >
          <template slot="SN" slot-scope="row">
            {{ row.index + 1 }}
          </template>

          <template slot="totInv" slot-scope="row">
            <span class="text-right d-block">{{ formatCurrency(row.value) }}</span>
          </template>

          <template slot="totExp" slot-scope="row">
            <span class="text-right d-block">{{ formatCurrency(row.value) }}</span>
          </template>

          <template slot="totProfitLossAmt" slot-scope="row">
            <span class="text-right d-block" :class="getProfitClass(row.value)">
              {{ formatCurrency(row.value) }}
            </span>
          </template>

          <template slot="profitLossPercent" slot-scope="row">
            <span class="text-right d-block" :class="getPercentageClass(row.value)">
              {{ row.value.toFixed(2) }}%
            </span>
          </template>

          <template slot="withoutModuleInv" slot-scope="row">
            <span class="text-right d-block">{{ formatCurrency(row.value) }}</span>
          </template>

          <template slot="withoutModuleExp" slot-scope="row">
            <span class="text-right d-block">{{ formatCurrency(row.value) }}</span>
          </template>

          <template slot="withoutModuleProfit" slot-scope="row">
            <span class="text-right d-block" :class="getProfitClass(row.value)">
              {{ formatCurrency(row.value) }}
            </span>
          </template>

          <template slot="withoutModuleProfitPercent" slot-scope="row">
            <span class="text-right d-block" :class="getPercentageClass(row.value)">
              {{ row.value.toFixed(2) }}%
            </span>
          </template>

          <template slot="status" slot-scope="row">
            <span class="label" :class="getStatusClass(row.item.profitLossPercent)">
              {{ row.item.profitLossPercent > 0 ? 'Profit' : row.item.profitLossPercent < 0 ? 'Loss' : 'Break Even' }}
            </span>
          </template>

          <template slot="bottom-row" v-if="summary">
            <td colspan="2" class="text-center font-weight-bold bg-light">FINAL TOTAL</td>
            <td class="text-right font-weight-bold bg-light">{{ formatCurrency(summary.totalInvoices) }}</td>
            <td class="text-right font-weight-bold bg-light">{{ formatCurrency(summary.totalExpenses) }}</td>
            <td class="text-right font-weight-bold bg-light" :class="getProfitClass(summary.totalProfit)">
              {{ formatCurrency(summary.totalProfit) }}
            </td>
            <td class="text-right font-weight-bold bg-light" :class="getPercentageClass(summary.overallProfitPercent)">
              {{ summary.overallProfitPercent.toFixed(2) }}%
            </td>
            <td colspan="5" class="bg-light"></td>
          </template>
        </b-table>

        <!-- Pagination -->
        <div class="d-flex justify-content-between align-items-center mt-3" v-if="totalRows > perPage">
          <div class="d-flex align-items-center">
            <label class="mr-2 mb-0">Show:</label>
            <b-form-select
              v-model="perPage"
              :options="pageOptions"
              size="sm"
              class="w-auto"
            ></b-form-select>
            <span class="ml-2 text-muted">entries</span>
          </div>

          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            size="sm"
            class="mb-0"
            first-number
            last-number
          ></b-pagination>

          <div class="text-muted small">
            Showing {{ (currentPage - 1) * perPage + 1 }} to {{ Math.min(currentPage * perPage, totalRows) }} of {{ totalRows }} entries
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import GrowthDashboardService from '../../../services/GrowthDashboardService';
import VueApexCharts from 'vue-apexcharts';
import * as XLSX from 'xlsx';
// Chart functionality temporarily disabled
// import Chart from 'chart.js';

export default {
  name: 'MonthlyGrowthComponent',
  components: {
    apexchart: VueApexCharts
  },
  props: {
    filters: {
      type: Object,
      required: true
    },
    isGraph: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      records: [],
      summary: null,
      // Pagination
      currentPage: 1,
      perPage: 50,
      pageOptions: [25, 50, 100, 200],
      totalRows: 0,
      sortBy: null,
      sortDesc: false,
      sortDirection: 'asc',
      filter: null
    };
  },

  watch: {
    records(newVal) {
      this.totalRows = newVal.length;
    }
  },

  computed: {
    paginatedRecords() {
      return this.records.slice(
        (this.currentPage - 1) * this.perPage,
        this.currentPage * this.perPage
      );
    },
    // Chart data for Revenue vs Expenses Line Chart
    revenueExpensesChartData() {
      if (!this.records || this.records.length === 0) {
        return {
          series: [],
          options: {}
        };
      }

      return {
        series: [
          {
            name: 'Total Invoices',
            data: this.records.map(record => parseFloat(record.totInv) || 0)
          },
          {
            name: 'Total Expenses',
            data: this.records.map(record => parseFloat(record.totExp) || 0)
          },
          {
            name: 'Net Profit/Loss',
            data: this.records.map(record => parseFloat(record.totProfitLossAmt) || 0)
          }
        ],
        options: {
          chart: {
            type: 'line',
            height: 350,
            toolbar: {
              show: true,
              tools: {
                download: true,
                zoom: true,
                zoomin: true,
                zoomout: true,
                pan: true,
                reset: true
              }
            }
          },
          colors: ['#008FFB', '#FF4560', '#00E396'],
          stroke: {
            curve: 'smooth',
            width: 3
          },
          xaxis: {
            categories: this.records.map(record => record.month),
            title: {
              text: 'Month'
            }
          },
          yaxis: {
            title: {
              text: 'Amount (₹)'
            },
            labels: {
              formatter: function(value) {
                return '₹' + (value / 100000).toFixed(0) + 'L';
              }
            }
          },
          legend: {
            position: 'top'
          },
          tooltip: {
            y: {
              formatter: function(value) {
                return '₹' + value.toLocaleString('en-IN');
              }
            }
          },
          markers: {
            size: 4,
            hover: {
              size: 6
            }
          }
        }
      };
    },

    // Chart data for Performance Distribution Pie Chart
    performanceDistributionChartData() {
      if (!this.records || this.records.length === 0) {
        return {
          series: [],
          options: {}
        };
      }

      const profitMonths = this.records.filter(record => record.profitLossPercent > 0).length;
      const lossMonths = this.records.filter(record => record.profitLossPercent < 0).length;
      const breakEvenMonths = this.records.filter(record => record.profitLossPercent === 0).length;

      return {
        series: [profitMonths, lossMonths, breakEvenMonths],
        options: {
          chart: {
            type: 'pie',
            height: 350
          },
          labels: ['Profit Months', 'Loss Months', 'Break Even'],
          colors: ['#00E396', '#FF4560', '#FEB019'],
          legend: {
            position: 'bottom'
          },
          tooltip: {
            y: {
              formatter: function(value) {
                return value + ' months';
              }
            }
          },
          responsive: [{
            breakpoint: 480,
            options: {
              chart: {
                width: 200
              },
              legend: {
                position: 'bottom'
              }
            }
          }]
        }
      };
    },

    // Chart data for Profit Percentage Trend
    profitPercentageChartData() {
      if (!this.records || this.records.length === 0) {
        return {
          series: [],
          options: {}
        };
      }

      return {
        series: [
          {
            name: 'Profit Percentage',
            data: this.records.map(record => parseFloat(record.profitLossPercent) || 0)
          },
          {
            name: 'Without Module %',
            data: this.records.map(record => parseFloat(record.withoutModuleProfitPercent) || 0)
          }
        ],
        options: {
          chart: {
            type: 'area',
            height: 350,
            toolbar: {
              show: true
            }
          },
          colors: ['#008FFB', '#775DD0'],
          stroke: {
            curve: 'smooth',
            width: 2
          },
          fill: {
            type: 'gradient',
            gradient: {
              shade: 'light',
              type: 'vertical',
              opacityFrom: 0.4,
              opacityTo: 0.1,
            }
          },
          xaxis: {
            categories: this.records.map(record => record.month),
            title: {
              text: 'Month'
            }
          },
          yaxis: {
            title: {
              text: 'Percentage (%)'
            },
            labels: {
              formatter: function(value) {
                return value.toFixed(1) + '%';
              }
            }
          },
          legend: {
            position: 'top'
          },
          tooltip: {
            y: {
              formatter: function(value) {
                return value.toFixed(2) + '%';
              }
            }
          },
          markers: {
            size: 4,
            hover: {
              size: 6
            }
          }
        }
      };
    },

    tableFields() {
      return [
        { key: 'SN', label: 'SN', thClass: 'text-center', tdClass: 'text-center' },
        { key: 'month', label: 'Month', thClass: 'text-center', tdClass: 'text-center' },
        { key: 'totInv', label: 'Total Invoices', thClass: 'text-center', tdClass: 'text-center' },
        { key: 'totExp', label: 'Total Expenses', thClass: 'text-center', tdClass: 'text-center' },
        { key: 'totProfitLossAmt', label: 'Net Profit/Loss', thClass: 'text-center', tdClass: 'text-center' },
        { key: 'profitLossPercent', label: 'Profit %', thClass: 'text-center', tdClass: 'text-center' },
        { key: 'withoutModuleInv', label: 'Without Module Inv', thClass: 'text-center', tdClass: 'text-center' },
        { key: 'withoutModuleExp', label: 'Without Module Exp', thClass: 'text-center', tdClass: 'text-center' },
        { key: 'withoutModuleProfit', label: 'Without Module Profit', thClass: 'text-center', tdClass: 'text-center' },
        { key: 'withoutModuleProfitPercent', label: 'Without Module %', thClass: 'text-center', tdClass: 'text-center' },
        { key: 'status', label: 'Status', thClass: 'text-center', tdClass: 'text-center' }
      ];
    },

    onFiltered(filteredItems) {
      if (filteredItems && Array.isArray(filteredItems)) {
        this.totalRows = filteredItems.length;
        this.currentPage = 1;
      }
    }
  },
  watch: {
    filters: {
      handler() {
        this.loadData();
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    notifyUser({ type = 'info', title = '', text = '' } = {}) {
      if (this.$bvToast) {
        const variantMap = { info: 'info', success: 'success', warning: 'warning', error: 'danger' };
        this.$bvToast.toast(text, {
          title: title || 'Info',
          variant: variantMap[type] || 'info',
          solid: true
        });
        return;
      }
      if (this.$notify) {
        this.$notify({ type, title: title || 'Info', text });
        return;
      }
      if (type === 'error') console.error(title, text);
      else console.log(title, text);
    },
    async loadData() {
      try {
        this.$emit('loading', true);

        const params = {};
        Object.keys(this.filters).forEach(key => {
          if (this.filters[key] !== null && this.filters[key] !== '') {
            params[key] = this.filters[key];
          }
        });

        const response = await GrowthDashboardService.getMonthlyGrowthData(params);

        if (response.data.success) {
          this.records = response.data.data.records;
          this.summary = response.data.data.summary;
          // Chart functionality temporarily disabled
          // this.$nextTick(() => {
          //   this.renderCharts();
          // });
        }
      } catch (error) {
        console.error('Error loading monthly growth data:', error);
        
      } finally {
        this.$emit('loading', false);
      }
    },

    // Chart functionality temporarily disabled
    renderCharts() {
      // Charts will be implemented when Chart.js is properly configured
      console.log('Chart rendering temporarily disabled');
    },

    renderRevenueExpensesChart() {
      // Chart functionality temporarily disabled
    },

    renderPerformanceDistributionChart() {
      // Chart functionality temporarily disabled
    },

    renderProfitPercentageChart() {
      // Chart functionality temporarily disabled
    },

    formatCurrency(amount) {
      if (amount === null || amount === undefined) return '₹0';
      return '₹' + Math.abs(amount).toLocaleString('en-IN');
    },

    getRowClass(record) {
      if (!record) return "";
      if (record.totProfitLossAmt < 0) return "danger";
      if (record.totProfitLossAmt > 0) return "success";
      return "";
    },

    getProfitClass(amount) {
      return amount >= 0 ? 'text-success' : 'text-danger';
    },

    getPercentageClass(percentage) {
      if (percentage > 0) return 'text-success';
      if (percentage < 0) return 'text-danger';
      return 'text-muted';
    },

    getStatusClass(percentage) {
      if (percentage > 0) return 'label-success';
      if (percentage < 0) return 'label-danger';
      return 'label-default';
    },

    async exportToExcel() {
      try {
        if (!this.records || this.records.length === 0) {
          this.notifyUser({
            type: 'warning',
            title: 'Export',
            text: 'No monthly growth data available to export.'
          });
          return;
        }

        const excelRows = this.records.map((record, index) => ({
          SN: index + 1,
          Month: record.month || '',
          'Total Invoices': record.totInv || 0,
          'Total Expenses': record.totExp || 0,
          'Net Profit/Loss': record.totProfitLossAmt || 0,
          'Profit %': record.profitLossPercent || 0,
          'Without Module Inv': record.withoutModuleInv || 0,
          'Without Module Exp': record.withoutModuleExp || 0,
          'Without Module Profit': record.withoutModuleProfit || 0,
          'Without Module %': record.withoutModuleProfitPercent || 0,
          Status: record.profitLossPercent > 0 ? 'Profit' : record.profitLossPercent < 0 ? 'Loss' : 'Break Even'
        }));

        const worksheet = XLSX.utils.json_to_sheet(excelRows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Monthly Growth');

        const timestamp = new Date().toISOString().slice(0, 10);
        XLSX.writeFile(workbook, `monthly_growth_dashboard_${timestamp}.xlsx`);
      } catch (error) {
        console.error('Error exporting to Excel:', error);
        this.notifyUser({
          type: 'error',
          title: 'Error',
          text: 'Failed to export monthly growth data'
        });
      }
    }
  },

  mounted() {
    // Load data on component mount
    this.loadData();
  },

  beforeDestroy() {
    // Chart functionality temporarily disabled
    // No chart cleanup needed
  }
};
</script>

<style scoped>
.table-responsive {
  overflow-x: auto;
}

.bg-gray {
  background-color: #f5f5f5 !important;
}

.label {
  display: inline-block;
  padding: 2px 6px;
  font-size: 11px;
  font-weight: bold;
  line-height: 1;
  color: #fff;
  text-align: center;
  white-space: nowrap;
  vertical-align: baseline;
  border-radius: 4px;
}

.label-success {
  background-color: #5cb85c;
}

.label-danger {
  background-color: #d9534f;
}

.label-default {
  background-color: #999;
}

.small-box {
  margin-bottom: 15px;
}

.text-right {
  text-align: right;
}

.text-success {
  color: #3c763d !important;
}

.text-danger {
  color: #a94442 !important;
}

.text-muted {
  color: #999 !important;
}
</style>



// WEBPACK FOOTER //
// MonthlyGrowthComponent.vue?550cf9b8